import { apiRequest } from './api'

// Data Inputs API service
export const dataInputsService = {
  // Get all data inputs
  getAll: async (params = {}) => {
    const queryParams = {}

    // Convert page/page_size to skip/limit
    if (params.page && params.page_size) {
      queryParams.skip = (params.page - 1) * params.page_size
      queryParams.limit = params.page_size
    } else if (params.page_size) {
      queryParams.limit = params.page_size
    }

    if (params.search) queryParams.search = params.search
    if (params.brand_id) queryParams.brand_id = params.brand_id
    if (params.status) {
      // Convert frontend status filter to backend format
      queryParams.is_active = params.status === 'active' ? 'true' : params.status === 'inactive' ? 'false' : undefined
    }

    const response = await apiRequest.get('/data-inputs/', { params: queryParams })
    return response.data
  },

  // Get data input by ID
  getById: async (id) => {
    const response = await apiRequest.get(`/data-inputs/${id}`)
    return response.data
  },

  // Create new data input
  create: async (dataInputData) => {
    const response = await apiRequest.post('/data-inputs/', dataInputData)
    return response.data
  },

  // Update data input
  update: async (id, dataInputData) => {
    const response = await apiRequest.put(`/data-inputs/${id}`, dataInputData)
    return response.data
  },

  // Delete data input (soft delete)
  delete: async (id) => {
    const response = await apiRequest.delete(`/data-inputs/${id}`)
    return response.data
  },

  // Get data input records
  getRecords: async (id, params = {}) => {
    const queryParams = new URLSearchParams()
    
    if (params.page) queryParams.append('page', params.page)
    if (params.size) queryParams.append('size', params.size)
    if (params.search) queryParams.append('search', params.search)
    if (params.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params.sort_order) queryParams.append('sort_order', params.sort_order)
    
    const url = `/data-inputs/${id}/records${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await apiRequest.get(url)
    return response.data
  },

  // Create record for data input
  createRecord: async (id, recordData) => {
    const response = await apiRequest.post(`/data-inputs/${id}/records`, recordData)
    return response.data
  },

  // Update record
  updateRecord: async (dataInputId, recordId, recordData) => {
    const response = await apiRequest.put(`/data-inputs/${dataInputId}/records/${recordId}`, recordData)
    return response.data
  },

  // Delete record
  deleteRecord: async (dataInputId, recordId) => {
    const response = await apiRequest.delete(`/data-inputs/${dataInputId}/records/${recordId}`)
    return response.data
  },

  // Get data input statistics
  getStats: async (id) => {
    const response = await apiRequest.get(`/data-inputs/${id}/stats`)
    return response.data
  },

  // Export data input records
  exportRecords: async (id, format = 'csv', params = {}) => {
    const queryParams = new URLSearchParams()
    queryParams.append('format', format)
    
    if (params.search) queryParams.append('search', params.search)
    if (params.date_from) queryParams.append('date_from', params.date_from)
    if (params.date_to) queryParams.append('date_to', params.date_to)
    
    const url = `/data-inputs/${id}/export?${queryParams.toString()}`
    const response = await apiRequest.get(url, {
      responseType: 'blob'
    })
    return response.data
  },

  // Get data inputs for dropdown/select
  getForSelect: async (brandId = null) => {
    const params = brandId ? `?brand_id=${brandId}&is_active=true` : '?is_active=true'
    const response = await apiRequest.get(`/data-inputs/${params}`)
    return response.data.map(dataInput => ({
      value: dataInput.id,
      label: dataInput.name,
      ...dataInput
    }))
  },

  // Attribute management methods
  // Get attributes for a data input
  getAttributes: async (dataInputId, includeInactive = false) => {
    const response = await apiRequest.get(`/data-inputs/${dataInputId}/attributes`, {
      params: { include_inactive: includeInactive }
    })
    return response.data
  },

  // Get only active attributes for forms
  getActiveAttributes: async (dataInputId) => {
    const response = await apiRequest.get(`/data-inputs/${dataInputId}/attributes/active`)
    return response.data
  },

  // Get a specific attribute
  getAttributeById: async (dataInputId, attributeId) => {
    const response = await apiRequest.get(`/data-inputs/${dataInputId}/attributes/${attributeId}`)
    return response.data
  },

  // Create a new attribute
  createAttribute: async (dataInputId, attributeData) => {
    const response = await apiRequest.post(`/data-inputs/${dataInputId}/attributes`, attributeData)
    return response.data
  },

  // Update an attribute
  updateAttribute: async (dataInputId, attributeId, attributeData) => {
    const response = await apiRequest.put(`/data-inputs/${dataInputId}/attributes/${attributeId}`, attributeData)
    return response.data
  },

  // Reorder attributes
  reorderAttributes: async (dataInputId, attributes) => {
    const reorderData = {
      attributes: attributes.map((attr, index) => ({
        id: attr.id,
        display_order: index
      }))
    }
    const response = await apiRequest.put(`/data-inputs/${dataInputId}/attributes/reorder`, reorderData)
    return response.data
  },

  // Record management methods
  // Get records for a data input with pagination
  getRecords: async (dataInputId, params = {}) => {
    const response = await apiRequest.get(`/data-inputs/${dataInputId}/records`, { params })
    return response.data
  },

  // Get a specific record
  getRecordById: async (dataInputId, recordId) => {
    const response = await apiRequest.get(`/data-inputs/${dataInputId}/records/${recordId}`)
    return response.data
  },

  // Create a new record
  createRecord: async (dataInputId, recordData) => {
    const response = await apiRequest.post(`/data-inputs/${dataInputId}/records`, recordData)
    return response.data
  },

  // Update a record
  updateRecord: async (dataInputId, recordId, recordData) => {
    const response = await apiRequest.put(`/data-inputs/${dataInputId}/records/${recordId}`, recordData)
    return response.data
  },

  // Delete a record
  deleteRecord: async (dataInputId, recordId) => {
    const response = await apiRequest.delete(`/data-inputs/${dataInputId}/records/${recordId}`)
    return response.data
  },

  // Bulk upload records from CSV
  bulkUploadRecords: async (dataInputId, file) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await apiRequest.post(`/data-inputs/${dataInputId}/records/bulk-upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Bulk update records
  bulkUpdateRecords: async (dataInputId, recordIds, updates) => {
    const response = await apiRequest.put(`/data-inputs/${dataInputId}/records/bulk-update`, {
      record_ids: recordIds,
      updates: updates,
      validate_data: true
    })
    return response.data
  },

  // Bulk delete records
  bulkDeleteRecords: async (dataInputId, recordIds) => {
    const response = await apiRequest.delete(`/data-inputs/${dataInputId}/records/bulk-delete`, {
      data: {
        record_ids: recordIds
      }
    })
    return response.data
  },

  // Advanced record filtering
  filterRecords: async (dataInputId, filterRequest) => {
    const response = await apiRequest.post(`/data-inputs/${dataInputId}/records/filter`, {
      data_input_id: dataInputId,
      ...filterRequest
    })
    return response.data
  },

  // Get all records for export (uses pagination to fetch all records)
  getAllRecordsForExport: async (dataInputId, options = {}) => {
    const allRecords = []
    let currentPage = 1
    let hasMore = true
    let pageSize = 1000 // Use maximum allowed limit

    while (hasMore) {
      let response

      // If we have column filters, use the advanced filtering endpoint
      if (options.filters && options.filters.length > 0) {
        response = await apiRequest.post(`/data-inputs/${dataInputId}/records/filter`, {
          data_input_id: dataInputId,
          filters: options.filters,
          search_query: options.search_query || null,
          skip: (currentPage - 1) * pageSize,
          limit: pageSize,
          include_total_count: true,
          include_deleted: false
        })
      } else {
        // Use basic records endpoint for simple cases (max 100 per page)
        const basicPageSize = 100 // Basic endpoint limit
        response = await apiRequest.get(`/data-inputs/${dataInputId}/records`, {
          params: {
            page: currentPage,
            limit: basicPageSize,
            search: options.search_query || undefined
          }
        })
        // Adjust pageSize for basic endpoint
        if (currentPage === 1) {
          // Update pageSize for subsequent iterations
          pageSize = basicPageSize
        }
      }

      const records = response.data?.records || response.records || []
      allRecords.push(...records)

      // Check if there are more records
      if (options.filters && options.filters.length > 0) {
        // Advanced filtering response
        hasMore = response.data?.has_next || false
      } else {
        // Basic records response
        const totalPages = response.data?.total_pages || 1
        hasMore = currentPage < totalPages
      }

      currentPage++

      // Safety check to prevent infinite loops
      if (currentPage > 1000) {
        console.warn('Export pagination limit reached (1000 pages)')
        break
      }
    }

    // Return in the same format as the original endpoints
    return {
      data: {
        records: allRecords
      },
      records: allRecords
    }
  },

  // Get unique values for a specific column
  getColumnUniqueValues: async (dataInputId, columnName, limit = 100) => {
    const response = await apiRequest.get(`/data-inputs/${dataInputId}/columns/${columnName}/unique-values`, {
      params: { limit }
    })
    return response.data
  }
}
